import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { EnabledFeature, OptimizelyFacade } from '@app/optimizely';
import {
  combineLoadingStates,
  DefaultRuleSet,
  LoadingState,
  LogicalOperator,
  NotificationType,
  OptimizelyFeatureFlag,
  OptimizelyFeatureSwitchMeta,
} from '@app/shared/interfaces';
import { Channel } from '@app/shared/models/channel';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { countWords } from '@app/shared/utils';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ConversationPosition,
  ConversationSimulation,
} from '../conversation-simulator/conversation-simulator.component';
import { SignupWizardService } from '../signup-wizard.service';

import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { ConfettiComponent } from '@app/ui/confetti/confetti.component';
import { ScheduleMeetingDialogComponent } from './schedule-meeting-dialog/schedule-meeting-dialog.component';

type Rules = {
  [key in DefaultRuleSet]?: {
    isEnabled: boolean;
    conversation?: ConversationSimulation[];
    totalHoursSaved?: number;
    label?: string;
  };
};

interface Cta {
  [key: number]: {
    label: () => void;
    action: () => void;
    button?: string | undefined;
  };
}

interface Ctas {
  primary: Cta;
  secondary: Cta;
}

type IEnabledFeature = EnabledFeature<OptimizelyFeatureFlag.SignupMessagingRules, OptimizelyFeatureSwitchMeta>;

@Component({
  standalone: false,
  selector: 'sbnb-signup-messaging-rules',
  templateUrl: './signup-messaging-rules.component.html',
  styleUrls: ['./signup-messaging-rules.component.scss', './signup-messaging-rules.component.mobile.scss'],
})
export class SignupMessagingRulesComponent implements OnInit {
  isMobile = false;
  shortCodes = {
    hostFirstName: '',
    listingName: 'The Cabin by the lake',
    // listingType: 'hosted',
    // listingFullAddress: 'The Cabin by the lake, Denver, Colorado',
    listingCity: 'Denver',
    guestFirstName: 'Gabriel',
    platform: Channel.Airbnb,
    checkIn: 'January 17th',
    checkOut: 'January 28th',
    // checkInHour: '12',
    checkOutHour: '4',
    guests: 3,
    nights: 10,
    totalPrice: '$2145',
    // houseManual: '',
    // listingDirections: '',
    // listingTransit: '',
    // houseRules: '',
  };
  defaultFeatureConfig: IEnabledFeature = {
    feature: OptimizelyFeatureFlag.SignupMessagingRules,
    meta: {
      [OptimizelyFeatureFlag.SignupMessagingRules]: {
        scenarios: [
          {
            key: 'hasLessThan2PropertiesAndNoRulesSelected',
            description: 'User has <4 properties and no rules selected',
            criteria: {
              properties: [0, 4],
              rulesSelected: [0],
              logicalOperator: LogicalOperator.AND,
            },
            primaryCta: 2,
            secondaryCta: 1,
          },
          {
            key: 'hasMoreThan2PropertiesAndNoRulesSelected',
            description: 'User has >4 properties and no rules selected',
            criteria: {
              properties: [5, 10000],
              rulesSelected: [0],
              logicalOperator: LogicalOperator.AND,
            },
            primaryCta: 2,
            secondaryCta: 2,
          },
          {
            key: 'hasPropertiesAndSomeMessagingRulesSelected',
            description: 'User has some properties and some rules selected',
            criteria: {
              properties: [0, 10000],
              rulesSelected: [0, 4],
              logicalOperator: LogicalOperator.AND,
            },
            primaryCta: 1,
            secondaryCta: 1,
          },
          {
            key: 'hasLessThan2PropertiesAnd5MessagingRulesSelected',
            description: 'User has some properties and 5 rules selected',
            criteria: {
              properties: [0, 4],
              rulesSelected: [5],
              logicalOperator: LogicalOperator.AND,
            },
            primaryCta: 3,
            secondaryCta: null,
          },
          {
            key: 'hasMoreThan2PropertiesAnd5MessagingRulesSelected',
            description: 'User has more properties and 5 rules selected',
            criteria: {
              properties: [5, 10000],
              rulesSelected: [5],
              logicalOperator: LogicalOperator.AND,
            },
            primaryCta: 3,
            secondaryCta: 2,
          },
        ],
      },
    },
  };

  noRuleSelectedConversation: ConversationSimulation[] = [
    {
      id: `defaultConversation-1`,
      text: `Hi, I’m interested to stay in your property from ${this.shortCodes.checkIn} till ${this.shortCodes.checkOut}.`,
      from: {
        initials: this.getGuestInitials(),
        channel: this.shortCodes.platform,
      },
      position: ConversationPosition.Left,
    },
    {
      id: `defaultConversation-2`,
      text: `I know it’s late in the night but we made a last minute plan and would love to confirm the booking before midnight!`,
      position: ConversationPosition.Left,
    },
    {
      id: `defaultConversation-3`,
      text: `Sorry, but we went with another property since we couldn’t wait any longer for the response.`,
      position: ConversationPosition.Left,
    },
  ];
  activeConversation: ConversationSimulation[];
  rules: Rules;
  ruleKeys: string[];

  ctas: Ctas;
  activeCtas: Ctas;
  defaultActiveCtas: Ctas;
  propertyCount: number;
  supportedChannelCount: number;
  activeRuleCount: number;
  totalHoursSaved = 0;
  potentialTotalHoursSaved: number;
  pageLoadingStateSubscription$: Subscription;
  pageLoadingState$: Observable<LoadingState>;
  savingLoadingState: LoadingState;
  featureConfig: IEnabledFeature;
  LoadingState = LoadingState;

  @ViewChild('confetti') confetti: ConfettiComponent;

  constructor(
    private wizard: SignupWizardService,
    private optimizelyFacade: OptimizelyFacade,
    private segmentIoService: SegmentIoService,
    private readonly breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private toast: ToastNotificationsService,

    private readonly router: Router
  ) {}

  ngOnInit() {
    if (!this.wizard.signupUser.token) {
      this.wizard.cancel();
      return;
    }

    this.listenForMobile();
    this.shortCodes.hostFirstName = this.wizard.signupUser.first_name;
    const propertyCount = this.wizard.signupUser.property_count;
    const supportedChannelCount = this.wizard.getSupportedChannelCount();

    this.propertyCount = propertyCount && propertyCount > 0 ? propertyCount : 1;

    this.supportedChannelCount = supportedChannelCount && supportedChannelCount > 0 ? supportedChannelCount : 1;

    this.initRules();

    this.pageLoadingState$ = combineLatest(
      this.optimizelyFacade.initLoadingState$,
      this.optimizelyFacade.enabledFeaturesLoadingState$
    ).pipe(
      map(([initLoadingState, optimizelyLoadingState]) =>
        combineLoadingStates([initLoadingState, optimizelyLoadingState])
      )
    );

    this.optimizelyFacade.init();

    this.initRules();
    this.initCtas();

    this.pageLoadingStateSubscription$ = this.pageLoadingState$.subscribe((loadingState) => {
      if (loadingState === LoadingState.Success) {
        this.featureConfig = this.optimizelyFacade.feature<IEnabledFeature>(OptimizelyFeatureFlag.SignupMessagingRules);
        this.getActiveRuleCount();
        this.determineActiveCtas();
      }

      if (loadingState === LoadingState.Error) {
        this.segmentIoService.track(SegmentEvent.OptimizelyError, {});
        // Use default feature switch value
        this.featureConfig = { ...{}, ...this.defaultFeatureConfig };
        this.determineActiveCtas();
      }
    });
  }

  initCtas() {
    this.ctas = {
      primary: {
        1: {
          label: () => {
            return `I just want to save ${this.totalHoursSaved}/${this.potentialTotalHoursSaved} hours`;
          },
          action: () => this.nextStep(1),
        },
        2: {
          label: () => {
            return `I don't want to save time`;
          },
          action: () => this.nextStep(2),
          button: 'secondary',
        },
        3: {
          label: () => {
            return `Let's go`;
          },
          action: () => this.nextStep(3),
        },
      },
      secondary: {
        1: {
          label: () => {
            return `Save me ${this.potentialTotalHoursSaved} hours`;
          },
          action: () => this.enableAllRules(),
        },
        2: {
          label: () => {
            return `Book a demo call`;
          },
          action: () => this.scheduleCall(),
        },
      },
    };

    this.defaultActiveCtas = {
      primary: this.ctas.primary[1],
      secondary: this.ctas.secondary[1],
    };
  }

  initRules() {
    this.rules = {
      [DefaultRuleSet.NewRequestToBook]: {
        isEnabled: true,
        conversation: [
          {
            id: `${DefaultRuleSet.CheckOutInstructions}-1`,
            text: `Hi, I’m interested to stay in your property from ${this.shortCodes.checkIn} till ${this.shortCodes.checkOut}.`,
            from: {
              initials: this.getGuestInitials(),
              channel: this.shortCodes.platform,
            },
            position: ConversationPosition.Left,
          },
          {
            id: `${DefaultRuleSet.CheckOutInstructions}-2`,
            text: `Hi ${this.shortCodes.guestFirstName}, and thanks for your interest in ${this.shortCodes.listingName}! <br/><br/>
I have received your request to book from ${this.shortCodes.checkIn}, with ${this.shortCodes.guests} guests for ${this.shortCodes.nights} nights. The total price would be ${this.shortCodes.totalPrice}. <br/><br/>
I will review your request and come back to you as soon as possible so that you can prepare for your trip accordingly. <br/><br/>
If you need any additional information, please do not hesitate to ask. I’d be happy to answer any questions you may have. <br/><br/>
Thanks,<br/>
${this.shortCodes.hostFirstName}
`,
            from: {
              initials: this.getHostInitials(),
            },
            position: ConversationPosition.Right,
            hint: 'Message Preview - Edit Later',
          },
        ],
      },
      [DefaultRuleSet.NewReservation]: {
        isEnabled: false,
        conversation: [
          {
            id: `${DefaultRuleSet.NewReservation}-1`,
            text: `Hey ${this.shortCodes.hostFirstName}, thanks for accepting my reservation. Excited to meet you!`,
            from: {
              initials: this.getGuestInitials(),
              channel: this.shortCodes.platform,
            },
            position: ConversationPosition.Left,
          },

          {
            id: `${DefaultRuleSet.NewReservation}-2`,
            text: `Hi ${this.shortCodes.guestFirstName}, and thanks for your reservation! <br/><br/>
I wanted to confirm your reservation at  ${this.shortCodes.listingName} starting on ${this.shortCodes.checkIn} for ${this.shortCodes.nights} with ${this.shortCodes.guests} guests. <br/><br/>
If you need any additional information, please do not hesitate to ask. I’d be happy to answer any questions you may have. <br/><br/>
I will contact you again a few days before your arrival to give you some additional instructions for a smooth check-in. <br/><br/>
I look forward to hosting you! <br/><br/>
Thanks,<br/>
${this.shortCodes.hostFirstName}
`,
            from: {
              initials: this.getHostInitials(),
            },
            position: ConversationPosition.Right,
            hint: 'Message Preview - Edit Later',
          },
        ],
      },
      [DefaultRuleSet.FirstMorning]: {
        isEnabled: false,
        conversation: [
          {
            id: `${DefaultRuleSet.FirstMorning}-1`,
            text: `Just checked in. Thanks for sending over the smart lock code, it made checkin-in super easy.`,
            from: {
              initials: this.getGuestInitials(),
              channel: this.shortCodes.platform,
            },
            position: ConversationPosition.Left,
          },
          {
            id: `${DefaultRuleSet.FirstMorning}-2`,
            text: `Hi ${this.shortCodes.guestFirstName}, <br/><br/>
I hope that you have settled in and that you are enjoying your stay. Please let me know if there is anything that you need or if I can assist you in any way.<br/><br/>
All the best and enjoy your stay! <br/>
${this.shortCodes.hostFirstName}
`,
            from: {
              initials: this.getHostInitials(),
            },
            position: ConversationPosition.Right,
            hint: 'Message Preview - Edit Later',
          },
        ],
      },
      [DefaultRuleSet.CheckOutInstructions]: {
        isEnabled: false,
        conversation: [
          {
            id: `${DefaultRuleSet.CheckOutInstructions}-1`,
            text: `We’ll be checking out tomorrow, is there anything you’d like us to take care of?`,
            from: {
              initials: this.getGuestInitials(),
              channel: this.shortCodes.platform,
            },
            position: ConversationPosition.Left,
          },
          {
            id: `${DefaultRuleSet.CheckOutInstructions}-2`,
            text: `Hi ${this.shortCodes.guestFirstName}, <br/><br/>
I hope that you are having an excellent stay so far at ${this.shortCodes.listingName}.<br/><br/>
I would like to remind you that check-out time is by ${this.shortCodes.checkOutHour}, unless explicitly agreed otherwise.<br/><br/>
Let me know if you have any questions.<br/><br/>
Safe travels,<br/>
${this.shortCodes.hostFirstName}
`,
            from: {
              initials: this.getHostInitials(),
            },
            position: ConversationPosition.Right,
            hint: 'Message Preview - Edit Later',
          },
        ],
      },
      [DefaultRuleSet.ReviewRequest]: {
        isEnabled: false,
        conversation: [
          {
            id: `${DefaultRuleSet.ReviewRequest}-1`,
            text: `We had such an amazing time at your property and city. Thanks for hosting us.`,
            from: {
              initials: this.getGuestInitials(),
              channel: this.shortCodes.platform,
            },
            position: ConversationPosition.Left,
          },
          {
            id: `${DefaultRuleSet.ReviewRequest}-2`,
            text: `Hi ${this.shortCodes.guestFirstName}, <br/><br/>
It was a pleasure hosting you in  ${this.shortCodes.listingCity}. We will certainly be leaving you a great review for your stay and I hope you have more great trips with ${this.shortCodes.platform}.<br/><br/>
If you ever come back, please let us know, we’d be happy to accommodate you again! <br/><br/>
If you ever have any suggestions for how we could have improved your stay, please do not hesitate to let us know right here, as we are constantly working to improve the experience for everyone. <br/><br/>
Thanks,<br/>
${this.shortCodes.hostFirstName}
`,
            from: {
              initials: this.getHostInitials(),
            },
            position: ConversationPosition.Right,
            hint: 'Message Preview - Edit Later',
          },
        ],
        label: '+ Save all the time     🎉',
      },
    };
    this.ruleKeys = Object.keys(this.rules);
    this.activeConversation = this.rules[DefaultRuleSet.NewRequestToBook].conversation;
    this.calculateHoursSavedForRules();
  }

  @HostListener('window:message', ['$event'])
  onMessage(event) {
    if (event && event.data && event.data.meetingBookSucceeded) {
      this.segmentIoService.track(SegmentEvent.SignUpScheduledSalesCall, this.getAnalyticsMeta());

      this.dialog.closeAll();
      this.toast.open('You have successfully booked a demo call.', 'Dismiss', NotificationType.Success);
    }
  }

  calculateHoursSavedForRules() {
    let potentialTotalHoursSaved = 0;

    this.ruleKeys.forEach((key) => {
      let wordCount = 0;

      this.rules[key].conversation.forEach((convo) => {
        wordCount = countWords(convo.text) + wordCount;
      });

      // (12 * Number of Properties * Number of Channels Selected & Supported * (Count of words in Rule / 19)) / 60
      const hoursSaved = Math.round((12 * this.propertyCount * this.supportedChannelCount * (wordCount / 19)) / 60);
      potentialTotalHoursSaved = potentialTotalHoursSaved + hoursSaved;

      this.rules[key] = { ...this.rules[key], ...{ hoursSaved } };
    });

    this.potentialTotalHoursSaved = potentialTotalHoursSaved;
  }

  listenForMobile() {
    this.breakpointObserver.observe(['(max-width: 960px)']).subscribe((state: BreakpointState) => {
      this.isMobile = state && state.matches ? true : false;
    });
  }

  calculateTotalHoursSaved() {
    let totalHoursSaved = 0;

    this.ruleKeys.forEach((key) => {
      totalHoursSaved = this.rules[key].isEnabled ? totalHoursSaved + this.rules[key].hoursSaved : totalHoursSaved;
    });

    this.totalHoursSaved = totalHoursSaved;
  }

  getHostInitials() {
    return Array.from(this.shortCodes.hostFirstName)[0];
  }

  getGuestInitials() {
    return Array.from(this.shortCodes.guestFirstName)[0];
  }

  determineActiveCtas() {
    const { scenarios } = this.featureConfig.meta[OptimizelyFeatureFlag.SignupMessagingRules];
    let activeScenario;

    if (scenarios) {
      for (let i = 0; i < scenarios.length; i++) {
        const scenario = scenarios[i];
        const { criteria } = scenario;
        const logicalOperator = scenario.criteria.logicalOperator.toUpperCase();

        if (
          logicalOperator === LogicalOperator.AND.toUpperCase() &&
          this.meetsPropertyCriteria(criteria.properties) &&
          this.meetsRulesSelectedCriteria(criteria.rulesSelected)
        ) {
          activeScenario = scenario;
          break;
        } else if (
          logicalOperator === LogicalOperator.OR.toUpperCase() &&
          (this.meetsPropertyCriteria(criteria.properties) || this.meetsRulesSelectedCriteria(criteria.rulesSelected))
        ) {
          activeScenario = scenario;
          break;
        }
      }
    }

    if (activeScenario) {
      this.activeCtas = {
        primary: activeScenario.primaryCta ? this.ctas.primary[+activeScenario.primaryCta] : null,
        secondary: activeScenario.secondaryCta ? this.ctas.secondary[+activeScenario.secondaryCta] : null,
      };
    }

    this.calculateTotalHoursSaved();
  }

  meetsPropertyCriteria(properties: number[]) {
    // Min Max comparison
    if (properties.length > 1) {
      return this.propertyCount >= properties[0] && this.propertyCount <= properties[1];
    }
    // Equality comparison
    if (properties.length === 1) {
      return this.propertyCount === +properties[0];
    }
  }

  meetsRulesSelectedCriteria(rulesSelected: number[]) {
    // Min Max comparison
    if (rulesSelected.length > 1) {
      return this.activeRuleCount >= rulesSelected[0] && this.activeRuleCount <= rulesSelected[1];
    }
    // Equality comparison
    if (rulesSelected.length === 1) {
      return this.activeRuleCount === +rulesSelected[0];
    }
  }

  toggleRule(selected: boolean, rule: DefaultRuleSet) {
    this.rules[rule].isEnabled = selected;
    this.activeConversation = selected ? [...this.rules[rule].conversation] : this.getClosestEnabledRuleCnversation();

    this.getActiveRuleCount();
    this.determineActiveCtas();
  }

  getClosestEnabledRuleCnversation() {
    let closestConversation;

    this.ruleKeys.forEach((key) => {
      if (this.rules[key].isEnabled) {
        closestConversation = this.rules[key].conversation;
      }
    });

    return closestConversation ? closestConversation : this.noRuleSelectedConversation;
  }

  enableAllRules() {
    this.ruleKeys.forEach((key) => {
      this.rules[key].isEnabled = true;
    });
    this.activeConversation = [...this.rules[DefaultRuleSet.ReviewRequest].conversation];
    this.getActiveRuleCount();
    this.determineActiveCtas();

    this.confetti.makeItRain();

    this.segmentIoService.track(SegmentEvent.SignUpClickedEnableAllRuleSets, this.getAnalyticsMeta());
  }

  nextStep(ctaNumber: number) {
    this.savingLoadingState = LoadingState.Pending;
    const rulesets = [] as DefaultRuleSet[];
    this.ruleKeys.forEach((key: DefaultRuleSet) => {
      this.rules[key].isEnabled ? rulesets.push(key) : null;
    });

    const cta = {
      number: ctaNumber,
      label: this.ctas.primary[ctaNumber].label(),
    };

    if (rulesets.length > 0) {
      this.wizard.updateRulesets(rulesets).subscribe(
        (res) => {
          this.savingLoadingState = LoadingState.Success;

          this.segmentIoService.track(SegmentEvent.SignUpSavedRuleSets, {
            ...{
              rulesets,
              cta,
            },
            ...this.getAnalyticsMeta(),
          });

          this.wizard.isAirbnbSignup()
            ? this.router.navigateByUrl('/user/register/review-listings')
            : this.wizard.next();
        },
        () => {
          this.segmentIoService.track(SegmentEvent.SignUpSavedRuleSetsError, {
            ...{
              rulesets,
              cta,
            },
            ...this.getAnalyticsMeta(),
          });

          this.savingLoadingState = LoadingState.Error;
        }
      );
    } else {
      this.segmentIoService.track(SegmentEvent.SignUpNoSavedRuleSets, {
        ...{
          cta,
        },
        ...this.getAnalyticsMeta(),
      });
      this.wizard.next();
    }
  }

  scheduleCall() {
    this.dialog.open(ScheduleMeetingDialogComponent, {
      width: '90vw',
      height: '90vh',
      panelClass: 'schedule-call--dialog',
      data: 'https://meetings.hubspot.com/brian-seelos/15-minute-discovery-call?embed=true&utm_campaign=SignUpFlow&utm_source=email&utm_medium=SignUpFlow&utm_content=SignUpFlow',
    });
  }

  getActiveRuleCount() {
    let activeRuleCount = 0;
    this.ruleKeys.forEach((key) => {
      if (this.rules[key].isEnabled) {
        activeRuleCount++;
      }
    });
    this.activeRuleCount = activeRuleCount;
  }

  getAnalyticsMeta() {
    return {
      propertyCount: this.propertyCount,
      totalHoursSaved: this.totalHoursSaved,
      potentialTotalHoursSaved: this.potentialTotalHoursSaved,
      activeRuleCount: this.activeRuleCount,
    };
  }
}
